/* =============================================================
   WHY US SECTION — Concrete differentiators with proof
   Addresses: Original "We're special and so are you" had zero proof
   Design: Split layout with image left, content right
   ============================================================= */

import { CheckCircle, TrendingUp, Clock, HeartHandshake } from "lucide-react";

const TEAM_IMAGE = "https://d2xsxph8kpxj0f.cloudfront.net/310519663505843393/2RHpeR2ZmtCHr4YUeyUmbN/hf_20260403_040015_92c45c79-537a-4a26-b4ce-92bd4218ff15_09fc9b30.jpg";

const differentiators = [
  {
    icon: CheckCircle,
    title: "Registered BAS Agent — Not Just a Bookkeeper",
    description: "As a registered BAS Agent (No. 262 458 37), we're legally authorised to prepare and lodge your BAS. Many bookkeepers can't do this.",
  },
  {
    icon: TrendingUp,
    title: "Xero Gold Partner with Real Expertise",
    description: "We don't just use Xero — we're certified advisors who can set it up, train your team, and customise it for your specific industry.",
  },
  {
    icon: Clock,
    title: "Weekly Reconciliation, Not Monthly",
    description: "Most bookkeepers reconcile monthly. We do it weekly, so you always have an accurate picture of your cash position.",
  },
  {
    icon: HeartHandshake,
    title: "A Real Person, Not a Call Centre",
    description: "You work directly with Agnieszka — your dedicated bookkeeper who knows your business inside out. No handoffs, no strangers.",
  },
];

export default function WhyUsSection() {
  return (
    <section id="about" className="py-20 lg:py-28 bg-[oklch(0.96_0.005_80)]">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Image with overlay card */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '3/4' }}>
              <img
                src={TEAM_IMAGE}
                alt="Perth bookkeeper consulting with a small business owner"
                className="w-full h-full object-cover"
                loading="lazy"
                width={400}
                height={520}
              />
            </div>
            {/* Floating achievement card */}
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-[oklch(0.22_0.06_250)] text-white rounded-xl p-5 shadow-xl max-w-[200px]">
              <div className="text-[oklch(0.72_0.12_75)] text-3xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                10+
              </div>
              <div className="text-white/80 text-sm leading-snug" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Years serving Perth small businesses
              </div>
            </div>
            {/* Gold accent bar */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[oklch(0.72_0.12_75)/0.15] rounded-2xl -z-10" />
          </div>

          {/* Right: Content */}
          <div>
            <div className="section-label mb-4">Why Choose Us</div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl text-[oklch(0.22_0.06_250)] mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="block">More Than a Bookkeeper</span>
              <span className="block italic text-[oklch(0.72_0.12_75)]">A Financial Partner</span>
            </h2>
            <p className="text-[oklch(0.45_0.02_250)] text-lg mb-8 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              Agnieszka founded The Perth Bookkeeper with one mission: to give Perth's small business owners the financial clarity and compliance they deserve — without the stress or confusion.
            </p>

            <div className="space-y-5">
              {differentiators.map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[oklch(0.72_0.12_75)/0.12] flex items-center justify-center mt-0.5">
                    <item.icon size={18} className="text-[oklch(0.72_0.12_75)]" />
                  </div>
                  <div>
                    <h3 className="text-[oklch(0.22_0.06_250)] font-semibold mb-1 text-base" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      {item.title}
                    </h3>
                    <p className="text-[oklch(0.45_0.02_250)] text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <a href="/about" className="btn-gold">
                Meet Agnieszka Peszel
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
